import React, { useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaCalendarAlt, FaSearch, FaFileExcel, FaFilePdf, FaMapMarkerAlt, FaUsers } from 'react-icons/fa';
import { Form, DatePicker, Row, Col, Button } from 'antd';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import Layout from '../../layout/Layout';
import Header from '../../components/Header';
import CardComponent from '../../components/cards/CardComponent';
import fetchData from '../../functions/fetchData';
import './PromoterSummary.css';
import dayjs from 'dayjs';
import logo from '../../assets/logo.png';
import axios from 'axios';
import { getHeaders } from '../../utility/getHeader';

export default function PromoterSummaryWithNshare() {
  const [form] = Form.useForm();
  const [dateRange, setDateRange] = useState(null);
  const [zone, setZone] = useState('Club');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState({
    grandTotal: 0,
    clubEntries: 0,
    finalEntries: 0,
    totalEntries: 0,
    uniqueNames: 0,
    perEntryAmount: 20
  });

  // Fetch promoter entries
  const handleSearch = async () => {
    if (!dateRange || dateRange.length !== 2) {
      toast.error('Please select date range');
      return;
    }

    const startDate = dateRange[0].format('YYYY-MM-DD');
    const endDate = dateRange[1].format('YYYY-MM-DD');

    setLoading(true);
    try {
      const response = await axios.post(
        `/account/promotor-sum-by-name`,
        { startDate, endDate },
        getHeaders()
      );

      if (response.data.success) {
        const { promotors, summary: apiSummary } = response.data.data;
        setReportData(promotors);
        
        // Calculate club entries, final entries, and total amount
        // Exclude Walking Customer, THAI, and UZbek from calculations
        const excludedPromoters = ['Walking Customer', 'THAI', 'UZbek'];
        let totalClubEntries = 0;
        let totalFinalEntries = 0;
        
        promotors.forEach(row => {
          const clubEntries = parseInt(row.club_entries || 2000);
          const isExcluded = excludedPromoters.some(name => 
            row.name && row.name.toLowerCase().trim() === name.toLowerCase().trim()
          );
          const finalEntries = isExcluded ? 0 : (parseInt(row.total_nos || 0) - clubEntries);
          totalClubEntries += clubEntries;
          totalFinalEntries += finalEntries;
        });
        
        setSummary({
          grandTotal: apiSummary.grandTotal,
          clubEntries: totalClubEntries,
          finalEntries: totalFinalEntries,
          totalEntries: apiSummary.totalEntries,
          uniqueNames: apiSummary.uniqueNames,
          perEntryAmount: 20
        });

        toast.success(`Found ${apiSummary.uniqueNames} promoters with ${apiSummary.grandTotal} total entries`);
      } else {
        setReportData([]);
        setSummary({ grandTotal: 0, clubEntries: 0, finalEntries: 0, totalEntries: 0, uniqueNames: 0, perEntryAmount: 20 });
        toast.info('No records found for selected criteria');
      }
    } catch (error) {
      console.error('Error fetching promoter data:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch promoter data');
      setReportData([]);
      setSummary({ grandTotal: 0, clubEntries: 0, finalEntries: 0, totalEntries: 0, uniqueNames: 0, perEntryAmount: 20 });
    } finally {
      setLoading(false);
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    if (reportData.length === 0) {
      toast.warning('No data to export');
      return;
    }

    const wb = XLSX.utils.book_new();
    
    // Create header data
    const headerData = [
      ['JANNAAT - PROMOTER SUMMARY REPORT WITH NSHARE'],
      [`Period: ${dateRange[0].format('DD/MM/YYYY')} to ${dateRange[1].format('DD/MM/YYYY')}`],
      [`Generated: ${dayjs().format('DD/MM/YYYY HH:mm:ss')}`],
      [],
      ['SUMMARY'],
      [`Total Promoters: ${summary.uniqueNames}`],
      [`Total Entries: ${summary.grandTotal}`],
      [`Total Records: ${summary.totalEntries}`],
      [`Per Entry Amount: ฿${summary.perEntryAmount}`],
      [`Total Amount: ฿${(summary.grandTotal * summary.perEntryAmount).toLocaleString()}`],
      [],
      ['#', 'Promoter Name', 'Total Entries', 'Club Entries', 'Final Entries', 'Amount (THB 20/entry)']
    ];

    // Add data rows
    const excludedPromoters = ['Walking Customer', 'THAI', 'UZbek'];
    const sortedData = [...reportData].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    const dataRows = sortedData.map((row, index) => {
      const totalNos = parseInt(row.total_nos || 0);
      const clubEntries = parseInt(row.club_entries || 2000);
      const isExcluded = excludedPromoters.some(name => 
        row.name && row.name.toLowerCase().trim() === name.toLowerCase().trim()
      );
      const finalEntries = isExcluded ? 0 : (totalNos - clubEntries);
      const amount = isExcluded ? 0 : (finalEntries * 20);
      
      return [
        index + 1,
        row.name,
        totalNos,
        clubEntries,
        finalEntries,
        amount
      ];
    });

    // Add total row
    const totalAmount = summary.finalEntries * 20;
    const companyShare = totalAmount * 0.30;
    const totalPayable = totalAmount * 0.70;
    
    dataRows.push([
      '',
      'GRAND TOTAL',
      summary.grandTotal,
      summary.clubEntries,
      summary.finalEntries,
      totalAmount
    ]);
    
    dataRows.push([
      '',
      '',
      '',
      '',
      'Company Share (30%)',
      companyShare
    ]);
    
    dataRows.push([
      '',
      '',
      '',
      '',
      'Total Payable Amount (70%)',
      totalPayable
    ]);

    const allData = [...headerData, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(allData);

    // Set column widths
    ws['!cols'] = [
      { wch: 5 },
      { wch: 25 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 20 }
    ];

    // Merge cells for header
    ws['!merges'] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }, // Title
      { s: { r: 1, c: 0 }, e: { r: 1, c: 5 } }, // Period
      { s: { r: 2, c: 0 }, e: { r: 2, c: 5 } }  // Generated
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Promoter Summary');
    const fileName = `Promoter_Summary_With_Nshare_${dateRange[0].format('YYYY-MM-DD')}_to_${dateRange[1].format('YYYY-MM-DD')}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success('Exported to Excel successfully');
  };

  // Export to PDF
  const exportToPDF = () => {
    if (reportData.length === 0) {
      toast.warning('No data to export');
      return;
    }

    const doc = new jsPDF();
    
    // Add logo
    try {
      doc.addImage(logo, 'PNG', 12, 6, 20, 20);
    } catch (error) {
      console.log('Logo not added:', error);
    }
    
    // Compact Header with soft light blue
    doc.setFillColor(225, 245, 254); // Soft light blue
    doc.rect(0, 0, 210, 28, 'F');
    
    doc.setTextColor(1, 87, 155); // Deep blue
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('JANNAAT', 38, 12);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text('PROMOTER SUMMARY REPORT WITH NSHARE', 38, 19);
    
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'normal');
    doc.text(`Period: ${dateRange[0].format('DD/MM/YYYY')} - ${dateRange[1].format('DD/MM/YYYY')}`, 38, 24);
    doc.text(`Generated: ${dayjs().format('DD/MM/YYYY HH:mm')}`, 145, 24);
    
    // Summary box - Compact and professional
    doc.setFillColor(240, 249, 255); // Very soft light blue
    doc.rect(10, 32, 190, 18, 'F');
    doc.setDrawColor(179, 229, 252); // Soft blue border
    doc.setLineWidth(0.3);
    doc.rect(10, 32, 190, 18, 'S');
    
    doc.setTextColor(1, 87, 155); // Deep blue
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('SUMMARY', 15, 37);
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text(`Promoters: ${summary.uniqueNames}`, 15, 42);
    doc.text(`Total Entries: ${summary.grandTotal}`, 55, 42);
    doc.text(`Final Entries: ${summary.finalEntries}`, 105, 42);
    doc.text(`Amount: THB ${(summary.finalEntries * 20).toLocaleString()}`, 155, 42);
    
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.text(`(@ THB ${summary.perEntryAmount}/entry)`, 15, 47);
    
    // Table data
    const excludedPromoters = ['Walking Customer', 'THAI', 'UZbek'];
    const sortedData = [...reportData].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    const tableData = sortedData.map((row, index) => {
      const totalNos = parseInt(row.total_nos || 0);
      const clubEntries = parseInt(row.club_entries || 2000);
      const isExcluded = excludedPromoters.some(name => 
        row.name && row.name.toLowerCase().trim() === name.toLowerCase().trim()
      );
      const finalEntries = isExcluded ? 0 : (totalNos - clubEntries);
      const amount = isExcluded ? 0 : (finalEntries * 20);
      
      return [
        index + 1,
        row.name,
        totalNos,
        clubEntries,
        finalEntries,
        `THB ${amount.toLocaleString()}`
      ];
    });

    // Add total row
    const totalAmount = (summary.finalEntries || 0) * 20;
    const companyShare = totalAmount * 0.30;
    const totalPayable = totalAmount * 0.70;
    
    tableData.push([
      { content: '', styles: { fillColor: [225, 245, 254] } },
      { content: 'GRAND TOTAL', styles: { fontStyle: 'bold', fillColor: [225, 245, 254], textColor: [1, 87, 155] } },
      { content: summary.grandTotal.toString(), styles: { fontStyle: 'bold', fillColor: [225, 245, 254], textColor: [1, 87, 155], halign: 'center' } },
      { content: (summary.clubEntries || 0).toString(), styles: { fontStyle: 'bold', fillColor: [225, 245, 254], textColor: [1, 87, 155], halign: 'center' } },
      { content: (summary.finalEntries || 0).toString(), styles: { fontStyle: 'bold', fillColor: [225, 245, 254], textColor: [1, 87, 155], halign: 'center' } },
      { content: `THB ${totalAmount.toLocaleString()}`, styles: { fontStyle: 'bold', fillColor: [225, 245, 254], textColor: [1, 87, 155] } }
    ]);
    
    tableData.push([
      { content: 'Company Share (30%)', styles: { fontStyle: 'bold', fillColor: [255, 249, 196], textColor: [245, 127, 23], halign: 'right' }, colSpan: 5 },
      { content: `THB ${companyShare.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`, styles: { fontStyle: 'bold', fillColor: [255, 249, 196], textColor: [245, 127, 23] } }
    ]);
    
    tableData.push([
      { content: 'Total Payable Amount (70%)', styles: { fontStyle: 'bold', fillColor: [200, 230, 201], textColor: [27, 94, 32], halign: 'right' }, colSpan: 5 },
      { content: `THB ${totalPayable.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`, styles: { fontStyle: 'bold', fillColor: [200, 230, 201], textColor: [27, 94, 32] } }
    ]);

    doc.autoTable({
      startY: 54,
      head: [['#', 'Promoter Name', 'Total Entries', 'Club Entries', 'Final Entries', 'Amount (THB 20/entry)']],
      body: tableData,
      theme: 'striped',
      headStyles: { 
        fillColor: [179, 229, 252], // Light blue gradient
        textColor: [1, 87, 155], // Deep blue font
        fontStyle: 'bold',
        fontSize: 8,
        halign: 'center',
        font: 'helvetica'
      },
      styles: { 
        fontSize: 8,
        cellPadding: 2.5,
        lineColor: [220, 220, 220],
        lineWidth: 0.1,
        font: 'helvetica'
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 10 },
        1: { halign: 'left', cellWidth: 60 },
        2: { halign: 'center', cellWidth: 28 },
        3: { halign: 'center', cellWidth: 28 },
        4: { halign: 'center', cellWidth: 28 },
        5: { halign: 'right', cellWidth: 35 }
      },
      alternateRowStyles: { fillColor: [240, 249, 255] },
      margin: { left: 10, right: 10 }
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setDrawColor(179, 229, 252); // Light blue
      doc.setLineWidth(0.3);
      doc.line(10, doc.internal.pageSize.height - 15, 200, doc.internal.pageSize.height - 15);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(120, 120, 120);
      doc.text(`Jannaat - Promoter Summary Report With Nshare`, 15, doc.internal.pageSize.height - 10);
      doc.text(
        `Page ${i} of ${pageCount}`,
        200,
        doc.internal.pageSize.height - 10,
        { align: 'right' }
      );
    }

    const pdfFileName = `Promoter_Summary_With_Nshare_${dateRange[0].format('YYYY-MM-DD')}_to_${dateRange[1].format('YYYY-MM-DD')}.pdf`;
    doc.save(pdfFileName);
    toast.success('Exported to PDF successfully');
  };

  // Reset filters
  const handleReset = () => {
    setDateRange(null);
    setZone('Club');
    setReportData([]);
    setSummary({ grandTotal: 0, clubEntries: 0, finalEntries: 0, totalEntries: 0, uniqueNames: 0, perEntryAmount: 20 });
    form.resetFields();
    toast.info('Filters reset');
  };

  return (
    <Layout>
      <Header title="Promoter Summary Report With Nshare" />
      <ToastContainer />

      <div className="promoter-summary-container">
        {/* Filter Section */}
        <CardComponent title="Search Filters" headerColor="light" className="filter-card-gradient">
          <Form form={form} layout="vertical">
            <Row gutter={16}>
              <Col xs={24} sm={12} md={8} lg={8}>
                <Form.Item
                  label={<><FaCalendarAlt className="me-2" />Select Date Range</>}
                  name="dateRange"
                  rules={[{ required: true, message: 'Please select a date range' }]}
                >
                  <DatePicker.RangePicker
                    style={{ width: '100%' }}
                    format="YYYY-MM-DD"
                    placeholder={['From', 'To']}
                    onChange={(dates) => setDateRange(dates)}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={4} lg={4}>
                <Form.Item label={<><FaMapMarkerAlt className="me-2" />Zone</>}>
                  <select
                    name="zone"
                    id="zone"
                    className="form-control"
                    value={zone}
                    onChange={(e) => setZone(e.target.value)}
                  >
                    <option value="Club">Club</option>
                    <option value="SkyBar">SkyBar</option>
                    <option value="Pool">Pool</option>
                  </select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12} lg={12}>
                <Form.Item label={<span style={{opacity: 0}}>Actions</span>}>
                  <div className="d-flex gap-2">
                    <Button
                      type="primary"
                      icon={<FaSearch />}
                      onClick={handleSearch}
                      loading={loading}
                      size="middle"
                      style={{borderRadius: '4px'}}
                    >
                      {loading ? 'Searching...' : 'Search'}
                    </Button>
                    <Button
                      onClick={handleReset}
                      disabled={loading}
                      size="middle"
                      style={{borderRadius: '4px'}}
                    >
                      Reset
                    </Button>
                    {reportData.length > 0 && (
                      <>
                        <Button
                          type="primary"
                          icon={<FaFileExcel />}
                          onClick={exportToExcel}
                          size="middle"
                          style={{borderRadius: '4px', background: '#52c41a', borderColor: '#52c41a'}}
                        >
                          Excel
                        </Button>
                        <Button
                          danger
                          type="primary"
                          icon={<FaFilePdf />}
                          onClick={exportToPDF}
                          size="middle"
                          style={{borderRadius: '4px'}}
                        >
                          PDF
                        </Button>
                      </>
                    )}
                  </div>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </CardComponent>

        {/* Summary Cards */}
        {reportData.length > 0 && (
          <div className="row g-3 mb-3">
            <div className="col-md-3">
              <div className="summary-card bg-primary">
                <div className="summary-icon">
                  <FaUsers />
                </div>
                <div className="summary-content">
                  <h6>Total Promoters</h6>
                  <h3>{summary.uniqueNames.toLocaleString()}</h3>
                </div>
              </div>
            </div>

            <div className="col-md-3">
              <div className="summary-card bg-success">
                <div className="summary-icon">
                  <FaCalendarAlt />
                </div>
                <div className="summary-content">
                  <h6>Total Entries</h6>
                  <h3>{summary.grandTotal.toLocaleString()}</h3>
                </div>
              </div>
            </div>

            <div className="col-md-3">
              <div className="summary-card bg-info">
                <div className="summary-icon">
                  <FaFileExcel />
                </div>
                <div className="summary-content">
                  <h6>Final Entries</h6>
                  <h3>{(summary.finalEntries || 0).toLocaleString()}</h3>
                </div>
              </div>
            </div>

            <div className="col-md-3">
              <div className="summary-card bg-warning">
                <div className="summary-icon">
                  <FaFilePdf />
                </div>
                <div className="summary-content">
                  <h6>Total Amount</h6>
                  <h3>THB {((summary.finalEntries || 0) * 20).toLocaleString()}</h3>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Data Table */}
        <CardComponent 
          title="Promoter Summary by Name With Nshare" 
          headerColor="light"
          className="data-card-gradient"
        >
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3 text-muted">Loading promoter data...</p>
            </div>
          ) : reportData.length === 0 ? (
            <div className="text-center py-5">
              <FaSearch size={50} className="text-muted mb-3" />
              <p className="text-muted">No data available. Select filters and click Search.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered table-hover table-sm compact-table">
                <thead className="table-primary">
                  <tr>
                    <th style={{width: '50px', color: 'white'}}>#</th>
                    <th style={{color: 'white'}}>Promoter Name</th>
                    <th className="text-center" style={{width: '120px', color: 'white'}}>Total Entries</th>
                    <th className="text-center" style={{width: '120px', color: 'white'}}>Club Entries</th>
                    <th className="text-center" style={{width: '120px', color: 'white'}}>Final Entries</th>
                    <th className="text-end" style={{width: '150px', color: 'white'}}>Amount (THB 20/entry)</th>
                  </tr>
                </thead>
                <tbody>
                  {[...reportData].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((row, index) => {
                    const excludedPromoters = ['Walking Customer', 'THAI', 'UZbek'];
                    const totalNos = parseInt(row.total_nos || 0);
                    const clubEntries = parseInt(row.club_entries || 2000);
                    const isExcluded = excludedPromoters.some(name => 
                      row.name && row.name.toLowerCase().trim() === name.toLowerCase().trim()
                    );
                    const finalEntries = isExcluded ? 0 : (totalNos - clubEntries);
                    const amount = isExcluded ? 0 : (finalEntries * 20);
                    
                    return (
                      <tr key={index}>
                        <td className="text-center">{index + 1}</td>
                        <td>{row.name}</td>
                        <td className="text-center fw-bold">{totalNos.toLocaleString()}</td>
                        <td className="text-center">{clubEntries.toLocaleString()}</td>
                        <td className="text-center fw-bold">{finalEntries.toLocaleString()}</td>
                        <td className="text-end fw-bold">
                          THB {amount.toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{backgroundColor: '#bbdefb', color: '#01579b'}}>
                    <th colSpan="2" className="text-end" style={{fontWeight: 'bold', padding: '12px'}}>GRAND TOTAL:</th>
                    <th className="text-center" style={{fontWeight: 'bold', padding: '12px'}}>{summary.grandTotal.toLocaleString()}</th>
                    <th className="text-center" style={{fontWeight: 'bold', padding: '12px'}}>{(summary.clubEntries || 0).toLocaleString()}</th>
                    <th className="text-center" style={{fontWeight: 'bold', padding: '12px'}}>{(summary.finalEntries || 0).toLocaleString()}</th>
                    <th className="text-end" style={{fontWeight: 'bold', padding: '12px'}}>THB {((summary.finalEntries || 0) * 20).toLocaleString()}</th>
                  </tr>
                  <tr style={{backgroundColor: '#fff9c4', color: '#f57f17'}}>
                    <th colSpan="5" className="text-end" style={{fontWeight: 'bold', padding: '12px', fontSize: '0.95rem'}}>Company Share (30%):</th>
                    <th className="text-end" style={{fontWeight: 'bold', padding: '12px', fontSize: '1.05rem'}}>THB {(((summary.finalEntries || 0) * 20) * 0.30).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</th>
                  </tr>
                  <tr style={{backgroundColor: '#c8e6c9', color: '#1b5e20'}}>
                    <th colSpan="5" className="text-end" style={{fontWeight: 'bold', padding: '12px', fontSize: '0.95rem'}}>Total Payable Amount (70%):</th>
                    <th className="text-end" style={{fontWeight: 'bold', padding: '12px', fontSize: '1.1rem'}}>THB {(((summary.finalEntries || 0) * 20) * 0.70).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</th>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardComponent>

        {/* Info Box */}
        {reportData.length > 0 && (
          <div className="alert alert-info mt-3" style={{background: 'linear-gradient(135deg, #e1f5fe 0%, #b3e5fc 100%)', border: '1px solid #81d4fa', color: '#01579b'}}>
            <strong>Note:</strong> Final entries are calculated as (Total Entries - Club Entries) at THB 20 per entry. Club entries default: 2000. 
            Total promoters: {summary.uniqueNames} | Total entries: {summary.grandTotal} | Final entries: {summary.finalEntries} | Total amount: THB {((summary.finalEntries || 0) * 20).toLocaleString()}
          </div>
        )}
      </div>
    </Layout>
  );
}
